import React, { useState, useEffect } from 'react';
import { Mail, Calendar, User, Clock, Phone, MessageSquare, DollarSign, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AttributionBlock from './AttributionBlock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsultationsView = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchConsultations(); }, []);

  const fetchConsultations = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/consultations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsultations(response.data.consultations || []);
    } catch (error) {
      toast.error('Failed to fetch consultations');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API}/admin/consultations/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bizenntrum_consultations_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('Failed to download CSV');
    }
  };

  const updateStatus = async (consultationId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/consultations/${consultationId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated successfully');
      fetchConsultations();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#059669';
      case 'completed': return '#2563eb';
      case 'cancelled': return '#dc2626';
      default: return '#f59e0b';
    }
  };

  if (loading) return <div className="loading-state">Loading consultations...</div>;

  return (
    <div className="consultations-view">
      <div className="view-header">
        <h2>Strategy Call Requests</h2>
        <button
          data-testid="consultations-csv-btn"
          onClick={downloadCsv}
          className="btn-download-csv"
        >
          <Download size={16} /> Download CSV
        </button>
      </div>

      {consultations.length === 0 ? (
        <p className="empty-state">No strategy call requests yet.</p>
      ) : (
        <div className="consultations-list">
          {consultations.map((consultation) => (
            <div key={consultation.id} className="consultation-card" data-testid={`consultation-card-${consultation.id}`}>
              <div className="consultation-header">
                <div className="consultation-info">
                  <div className="consultation-name">
                    <User size={18} />
                    <strong>{consultation.name}</strong>
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(consultation.status) }}
                    >
                      {consultation.status}
                    </span>
                  </div>
                  <div className="consultation-meta">
                    <span><Mail size={14} /> {consultation.email}</span>
                    {consultation.phone && (
                      <span><Phone size={14} /> {consultation.phone}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="consultation-details">
                <div className="detail-row">
                  <span className="detail-label"><Calendar size={14} /> Preferred Date:</span>
                  <span className="detail-value">{consultation.preferred_date || 'Not specified'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label"><Clock size={14} /> Preferred Time:</span>
                  <span className="detail-value">{consultation.preferred_time || 'Not specified'}</span>
                </div>
                {consultation.service && (
                  <div className="detail-row">
                    <span className="detail-label"><DollarSign size={14} /> Revenue Range:</span>
                    <span className="detail-value highlight">{consultation.service}</span>
                  </div>
                )}
                {consultation.message && (
                  <div className="detail-row message-row">
                    <span className="detail-label"><MessageSquare size={14} /> Message:</span>
                    <span className="detail-value">{consultation.message}</span>
                  </div>
                )}
              </div>

              <AttributionBlock record={consultation} />

              <div className="consultation-footer">
                <div className="consultation-status">
                  <label>Status:</label>
                  <select
                    value={consultation.status}
                    onChange={(e) => updateStatus(consultation.id, e.target.value)}
                    className="status-select"
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="consultation-date">
                  Submitted: {formatDate(consultation.created_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConsultationsView;
